/* eslint-disable no-unused-vars */
import { TagHandlerBase } from './tagHandlerBase';
import store from '@/store';
import { log as debugLog, warn as debugWarn, error as debugError } from '@/utils/debugLogger';
const THREE = window.THREE;

/**
 * Handler for AVA Inspect tags
 */
export class AvaInspectHandler extends TagHandlerBase {
    /**
     * Check if tag type should be processed
     * @param {string} type - Tag type
     * @param {Object} displaySettings - Display settings
     * @returns {boolean} Should process AVA Inspect
     */
    shouldProcess(type, displaySettings) {
        return displaySettings.showAvaInspect && type === 'ava-inspect';
    }

    /**
     * Collect AVA Inspect data
     * @param {Object} settings - Display settings
     * @param {Object} viewerData - Viewer position data
     * @returns {Object} Collected AVA Inspect data
     */
    collectData(settings, viewerData) {
        const { isGlobalSearchFilter, displaySettings } = settings;
        const data = {};

        // Clear any existing cluster text meshes
        this.clearClusterTextMeshes();

        // Get tag type from extension
        const type = this.extension.currentTagType || 'ava-inspect';

        // Skip if not applicable
        if (!this.shouldProcess(type, displaySettings)) return data;

        let avaInspectTags = store.getters.GET_AVA_INSPECT_TAGS;

        if (isGlobalSearchFilter) {
            let isolatedTags = store.getters['GET_ISOLATED_IDS'];
            if (isolatedTags.size > 0) {
                avaInspectTags = avaInspectTags.filter(t => isolatedTags.has(t.id));
            }
        }

        // Update with editing tags if in 2D view
        if (this.isViewer2d()) {
            avaInspectTags = store.state.forgeService.getAvaInspectTagsInLevel(store.state.selectedLevel);
        }
        
        avaInspectTags = store.state.forgeService.updateListWithEditingTag(avaInspectTags);

        // Only apply clustering after filtering in 2D view
        if (this.isViewer2d() && avaInspectTags.length > 0) {
            // Apply clustering logic
            const { items, clusters } = this.getTagsClustersAndItems2D(avaInspectTags, viewerData.position);

            // Process clusters
            this.processClusters(clusters, data, 'avaInspect-cluster');

            // Update to use filtered items
            avaInspectTags = items;
        }

        avaInspectTags.forEach(tag => {
            // Get point in viewer space
            let point = this.getPointForAvaInspect(tag);
            if (!point) return;

            // Create point cloud data
            const pcData = this.createAvaInspectData(tag, point);

            // Add to collection (using 'avaInspect' as the key for green pins)
            data['avaInspect'] ? data['avaInspect'].push(pcData) : data['avaInspect'] = [pcData];

            // Draw tag area if applicable
            this.drawTagArea(tag);
        });

        return data;
    }

    /**
     * Get point in viewer space for AVA Inspect tag
     * @param {Object} tag - AVA Inspect tag object
     * @returns {Object|null} Point in viewer space
     */
    getPointForAvaInspect(tag) {
        if (!tag) {
            debugWarn('avaInspectHandler', 'AVA Inspect tag is null or undefined');
            return null;
        }

        let point;

        if (this.isViewer2d()) {
            point = store.state.selectedLevel.mappingViewersPointsInfo._3dTo2d(tag.point);
            point.z = .1;
        } else {
            point = tag.getPointInView();

            // Skip if outside crop box in 3D view
            if (!store.state.forgeService.getMainExtension()?.isWithinCropBox(point)) {
                return null;
            }
        }

        return point;
    }

    /**
     * Create AVA Inspect data for point cloud
     * @param {Object} tag - AVA Inspect tag object
     * @param {Object} point - Point in viewer space
     * @returns {Object} Point cloud data
     */
    createAvaInspectData(tag, point) {
        const _this = this;
        return {
            name: tag.name,
            thumbnailURL: tag.thumbnailUrl,
            object: tag,
            point: point,
            onClick: (event) => {
                store.dispatch('FETCH_AVA_INSPECT_TAG_BY_ID', tag.id).then(fetchedTag => {
                    store.state.forgeService.onClickOn3dIcon(fetchedTag, event, _this.extension.viewType);
                });
            }
        };
    }
}
